// src/arena/ArenaBlock.jsx
// ══════════════════════════════════════════════════════════════════════════════
// 🥊 LA ARENA — el bloque final.
//
// Los bloques anteriores enseñan la forma. El del Laboratorio de Voz la dice en
// voz alta contra un texto conocido. Este es el único donde no hay texto, no hay
// respuesta correcta y no hay profesor: hay dos minutos y alguien con un
// problema.
//
// ── SPACY ES EL PROTAGONISTA, PERO NO HACE DE PROFESOR ───────────────────────
// La cara es la de siempre —los mismos GIFs que en las lecciones— y eso importa:
// el alumno ya confía en ella. Lo que cambia es el PAPEL. Aquí Spacy se disfraza
// del personaje que el alumno tiene que enfrentar en su trabajo, y la pantalla
// lo dice sin ambigüedad ("Spacy se convierte en: un cliente impaciente").
//
// Por eso la cara ocupa el centro y crece cuando habla, en vez de vivir en una
// esquina como en un chat. Durante dos minutos el alumno no lee: escucha y
// contesta. Si hay algo que mirar que no sea la cara, mira eso, y deja de
// escuchar.
//
// ── LO QUE NO SE ENSEÑA EN PANTALLA ──────────────────────────────────────────
// No se muestra lo que dice el personaje. Es deliberado: leer sus frases
// convierte esto en un chat con voz y el alumno acabaría leyendo en vez de
// escuchando. Lo único que aparece es lo que dijo ÉL, que es lo que necesita
// para saber si se le entendió.
// ══════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';
import { useArenaVoice } from './useArenaVoice.js';
import './arena.css';

// ── SOLO TRES CARAS ──────────────────────────────────────────────────────────
// Mudo, hablando y error. En las lecciones Spacy tiene cinco estados porque hay
// tiempos muertos que llenar; aquí la conversación no para, y cambiar de GIF
// cada dos segundos convierte la cara en un semáforo que distrae de lo único
// que importa, que es escuchar.
//
// De quién es el turno lo dice el ANILLO, que es CSS y cambia sin recargar una
// imagen. La cara solo se mueve cuando Spacy habla.
// ── Video en vez de gif ─────────────────────────────────────────────────────
// Los gifs de Spacy son de 1080x1080 y pesan 44 MB entre todos; el de hablar,
// el solo, 26,5 MB. Los mismos videos a 512 px pesan 664 KB entre todos y a
// tamano de pantalla se ven igual. Un gif no comprime entre fotogramas —guarda
// cada cuadro casi entero—, un video si. Los .gif siguen en assets/ por si
// hiciera falta volver.
import spacyNormal from '../assets/spacy-normal.mp4';
import spacyHablando from '../assets/spacy-hablando.mp4';
import spacyError from '../assets/spacy-error.mp4';

// El personaje viene del backend en inglés porque es lo que lee el modelo. Para
// la pantalla se traduce: el alumno tiene que entender de un vistazo a quién se
// enfrenta, y ese vistazo es en su idioma.
const PAPELES = {
    cliente_molesto: { es: 'un cliente impaciente', emoji: '😤' },
    jefe_exigente: { es: 'un jefe con prisa', emoji: '⏱️' },
    recepcion_hotel: { es: 'un recepcionista de hotel', emoji: '🛎️' },
    entrevistador: { es: 'un entrevistador', emoji: '💼' },
    paciente: { es: 'un paciente preocupado', emoji: '🩺' },
    proveedor: { es: 'un proveedor que va tarde', emoji: '📦' },
    camarero: { es: 'un camarero con prisa', emoji: '🍽️' },
};

// ── LA CARA ──────────────────────────────────────────────────────────────────
// El anillo reacciona al turno: gira y se enciende cuando Spacy habla, se pone
// verde cuando escucha. Es lo mismo que hace el orbe del agente de Deepgram, y
// funciona por la misma razón — a un metro de distancia se ve de quién es el
// turno sin leer una palabra.
function CaraDeSpacy({ gif, modo, tamano = 'grande' }) {
    return (
        <div className={`arn-cara arn-cara-${tamano} es-${modo}`}>
            <div className="arn-anillo" aria-hidden="true" />
            <div className="arn-cara-marco">
                <video
                  key={gif}
                  src={gif}
                  className="arn-cara-gif"
                  autoPlay
                  loop
                  muted
                  playsInline
                  aria-label="Spacy"
                />
            </div>
        </div>
    );
}

// ── UNA CAJA DE TRANSCRIPCIÓN ────────────────────────────────────────────────
// Tres líneas visibles y el resto por scroll, siempre pegada al final.
//
// El auto-scroll mueve el scroll DE LA CAJA, no la página. Con
// `scrollIntoView` el navegador arrastra el contenedor más cercano que pueda
// desplazarse, y en mitad de una lección eso significa mover la lección entera
// bajo los pies del alumno cada vez que alguien dice una frase.
function Transcripcion({ etiqueta, lineas, vacio, tono }) {
    const cajaRef = useRef(null);

    useEffect(() => {
        const caja = cajaRef.current;
        if (caja) caja.scrollTop = caja.scrollHeight;
    }, [lineas]);

    return (
        <div className={`arn-transcripcion arn-tono-${tono}`}>
            <span className="arn-etiqueta">{etiqueta}</span>
            <div className="arn-lineas" ref={cajaRef}>
                {lineas.length === 0
                    ? <p className="arn-linea-vacia">{vacio}</p>
                    : lineas.map((t, i) => (
                        <p key={i} className="arn-linea">{t}</p>
                    ))}
            </div>
        </div>
    );
}

// ── EL RELOJ ─────────────────────────────────────────────────────────────────
// Un anillo, no un número suelto: la presión del tiempo se siente mejor viendo
// menguar algo que leyendo cifras. Se pone rojo en los últimos 20 segundos.
function Reloj({ restante, total }) {
    const R = 26;
    const vuelta = 2 * Math.PI * R;
    const parte = total > 0 ? Math.max(0, Math.min(1, restante / total)) : 0;
    const urgente = restante <= 20;

    const mm = Math.floor(restante / 60);
    const ss = String(restante % 60).padStart(2, '0');

    return (
        <div className={`arn-reloj${urgente ? ' es-urgente' : ''}`}>
            <svg viewBox="0 0 64 64" aria-hidden="true">
                <circle cx="32" cy="32" r={R} className="arn-reloj-pista" />
                <circle
                    cx="32" cy="32" r={R}
                    className="arn-reloj-aguja"
                    strokeDasharray={vuelta}
                    strokeDashoffset={vuelta * (1 - parte)}
                />
            </svg>
            <span className="arn-reloj-cifra">{mm}:{ss}</span>
        </div>
    );
}

// ── EL RECIBO DE SUDOR ───────────────────────────────────────────────────────
function Recibo({ datos, onCerrar }) {
    const v = datos?.veredicto || null;
    const inm = datos?.inmersion || {};
    const logrado = !!v?.logrado;
    const pct = inm.inmersion ?? 0;

    // La barra sube desde cero al aparecer. Ver subir el número es la mitad de
    // la recompensa; que ya esté puesto no se siente como haberlo ganado.
    const [ancho, setAncho] = useState(0);
    useEffect(() => {
        const t = setTimeout(() => setAncho(pct), 250);
        return () => clearTimeout(t);
    }, [pct]);

    return (
        <div className="arn-recibo">
            <CaraDeSpacy gif={logrado ? spacyHablando : spacyNormal} modo="quieto" tamano="mini" />

            <div className={`arn-sello${logrado ? ' es-logrado' : ''}`}>
                <span className="arn-sello-icono">{logrado ? '✓' : '×'}</span>
                <div>
                    <div className="arn-etiqueta">Misión</div>
                    <div className="arn-sello-texto">{logrado ? 'Resuelta' : 'No resuelta'}</div>
                </div>
            </div>

            {v?.por_que && <p className="arn-porque">{v.por_que}</p>}

            {/* La inmersión es LA métrica de la Arena y la única que el alumno
                puede ver subir semana a semana. Va grande y sola. */}
            <div className="arn-inmersion">
                <div className="arn-inmersion-cabecera">
                    <span className="arn-etiqueta">Tu inmersión</span>
                    <span className="arn-inmersion-cifra">{pct}<small>%</small></span>
                </div>
                <div className="arn-barra">
                    <div className="arn-barra-relleno" style={{ width: `${ancho}%` }} />
                </div>
                <div className="arn-inmersion-pie">
                    {inm.ingles ?? 0} palabras en inglés · {inm.espanol ?? 0} en español
                    {datos?.turnos ? ` · ${datos.turnos} turnos` : ''}
                </div>
            </div>

            {/* UN error. Uno solo, y por eso ocupa su propio bloque: cinco
                errores no se recuerdan ninguno. */}
            {v?.un_error && (
                <div className="arn-ficha arn-ficha-error">
                    <span className="arn-etiqueta">Lo que arreglas mañana</span>
                    <p>{v.un_error}</p>
                </div>
            )}

            {v?.una_frase && (
                <div className="arn-ficha arn-ficha-frase">
                    <span className="arn-etiqueta">La frase que te faltaba</span>
                    <p className="arn-en">{v.una_frase}</p>
                </div>
            )}

            {v?.momento_clave && (
                <div className="arn-ficha arn-ficha-bien">
                    <span className="arn-etiqueta">Tu mejor momento</span>
                    <p className="arn-en">{v.momento_clave}</p>
                </div>
            )}

            {!v && (
                <p className="arn-aviso">
                    No se pudo generar el veredicto esta vez, pero tu inmersión sí quedó medida.
                </p>
            )}

            <button className="arn-boton arn-boton-primario" onClick={onCerrar}>
                Terminar bloque
            </button>
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════════════
//  EL BLOQUE
// ══════════════════════════════════════════════════════════════════════════════
export default function ArenaBlock({
    exercise,
    onCorrect,
    onSkip,
    nivel = 'B1',
    contexto = '',
    objetivo = '',
    semilla,
    // academico | contextual. Se llama asi y no `modo` porque ahi abajo `modo`
    // ya es el estado visual de la cara, y dos cosas distintas con el mismo
    // nombre en el mismo archivo es como se escriben los errores tontos.
    modoClase = 'contextual',
}) {
    const {
        estado, mision, restante, misTurnos, susTurnos, sugerencias,
        hablando, oyendo, veredicto, error, entrar, salir,
    } = useArenaVoice();

    // ── NORMAL O ASISTIDO ────────────────────────────────────────────────────
    // Se decide AQUI, al entrar, y no en la configuración de la clase. Cuando
    // el alumno eligió el nivel y su profesión todavía no sabía qué era esto;
    // ahora tiene el personaje delante y sí puede decidir si quiere el andamio.
    //
    // Se recuerda entre misiones porque es una preferencia de cómo aprende, no
    // una decisión por sesión: a quien le hace falta le hace falta hoy y
    // mañana, y volver a preguntárselo cada vez es fricción.
    const [asistido, setAsistido] = useState(() => {
        try { return localStorage.getItem('arena_asistido') === '1'; } catch { return false; }
    });

    const cambiarModo = (valor) => {
        setAsistido(valor);
        try { localStorage.setItem('arena_asistido', valor ? '1' : '0'); } catch { /* sin memoria, se queda en esta sesión */ }
    };

    // ── EL CONTEXTO DE HOY ───────────────────────────────────────────────────
    // La configuración de la clase dice a qué se dedica el alumno, y está bien
    // como punto de partida. Pero hoy puede querer otra cosa: puso "Viajes"
    // porque viaja, y esta tarde quiere pedir la cena en un restaurante.
    //
    // Si escribe algo, SUSTITUYE al contexto de la clase en vez de sumarse. Si
    // se sumara, "Viajes" seguiría pesando y le saldría el recepcionista del
    // hotel otra vez — que es justo lo que no pidió.
    const [contextoHoy, setContextoHoy] = useState('');

    const [total, setTotal] = useState(120);

    // Los GIFs se precargan. Si no, el primer cambio de estado enseña un hueco
    // en blanco justo en el momento en que Spacy empieza a hablar.
    useEffect(() => {
        [spacyNormal, spacyHablando, spacyError]
            .forEach((src) => { const i = new Image(); i.src = src; });
    }, []);

    useEffect(() => {
        if (mision?.segundos) setTotal(mision.segundos);
    }, [mision]);

    const arrancar = () => entrar({
        nivel,
        contexto: contextoHoy.trim() || contexto,
        objetivo,
        semilla,
        asistido,
        modo: modoClase,
    });

    // La Arena no se aprueba ni se suspende: se atraviesa. Aunque el personaje
    // no quede satisfecho, el alumno estuvo dos minutos hablando inglés bajo
    // presión, que es lo que el bloque pedía. Cerrar en falso aquí castigaría
    // justo la conducta que se quiere premiar.
    const cerrar = () => (onCorrect ? onCorrect() : onSkip?.());

    // El personaje puede venir de dos sitios y el orden importa:
    //   1. Generado a medida -> trae su propio nombre en español y su emoji.
    //   2. De la lista de reserva -> se traduce por id con PAPELES.
    // Si no hay ninguno, se enseña el inglés antes que dejar el hueco vacío.
    const papel = mision?.personaje_es
        ? { es: mision.personaje_es, emoji: mision.emoji || '🎭' }
        : (PAPELES[mision?.escenario] || null);

    const gif = estado === 'error' ? spacyError
        : hablando ? spacyHablando
            : spacyNormal;

    const modo = estado === 'error' ? 'error'
        : hablando ? 'hablando'
            : oyendo ? 'oyendo'
                : estado === 'conectando' || estado === 'cerrando' ? 'pensando'
                    : 'quieto';

    return (
        <div className="arn-raiz">

            {/* ── ANTES DE ENTRAR ─────────────────────────────────────────── */}
            {(estado === 'listo' || estado === 'error') && (
                <div className="arn-briefing">
                    <CaraDeSpacy gif={gif} modo={modo} />

                    <div className="arn-marca">
                        <span className="arn-marca-punto" />
                        La Arena
                    </div>

                    <h2 className="arn-titulo">
                        Dos minutos.<br />Una persona real.
                    </h2>

                    <p className="arn-entradilla">
                        {exercise?.instruction
                            || 'Aquí no hay ejercicios. Alguien tiene un problema y tú tienes que resolverlo en inglés, hablando.'}
                    </p>

                    <ul className="arn-reglas">
                        <li><b>Spacy se disfraza.</b> Hoy no es tu profesor.</li>
                        <li><b>No te va a corregir.</b> Si te equivocas, sigue la conversación.</li>
                        <li><b>No cambia a español.</b> Aunque tú lo hagas.</li>
                        <li><b>Puedes interrumpirle.</b> Habla y se calla, como una persona.</li>
                    </ul>

                    <div className="arn-modos">
                        <span className="arn-etiqueta">Cómo quieres entrar</span>
                        <div className="arn-modos-fila">
                            {[
                                { v: false, e: '⚡', t: 'Normal', d: 'Sin ayudas' },
                                { v: true, e: '🧩', t: 'Asistido', d: 'Con ideas de qué decir' },
                            ].map(({ v, e, t, d }) => (
                                <button
                                    key={String(v)}
                                    type="button"
                                    className={`arn-modo${asistido === v ? ' es-activo' : ''}`}
                                    aria-pressed={asistido === v}
                                    onClick={() => cambiarModo(v)}
                                >
                                    <span className="arn-modo-titulo">{e} {t}</span>
                                    <span className="arn-modo-desc">{d}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Tu configuración, y la puerta para cambiarla.
                        Se enseña lo que eligió al empezar la clase para que no
                        tenga que acordarse, y debajo un campo por si hoy quiere
                        otra cosa. Vacío = se entra con lo de siempre: quien no
                        quiera tocar nada solo pulsa el botón. */}
                    <div className="arn-config">
                        <span className="arn-etiqueta">Tu configuración</span>
                        <p className="arn-config-actual">
                            <b>{nivel}</b>{contexto ? ` · ${contexto}` : ''}
                        </p>
                        <input
                            className="arn-config-input"
                            type="text"
                            value={contextoHoy}
                            maxLength={160}
                            onChange={(e) => setContextoHoy(e.target.value)}
                            placeholder="¿Hoy prefieres otra cosa? Ej: pedir la cena a un camarero"
                            aria-label="Cambiar el contexto solo para esta misión"
                        />
                        {contextoHoy.trim() && (
                            <p className="arn-config-nota">
                                Solo para esta misión. Tu configuración de la clase no cambia.
                            </p>
                        )}
                    </div>

                    {error && <p className="arn-error">{error}</p>}

                    <div className="arn-botones">
                        <button className="arn-boton arn-boton-primario" onClick={arrancar}>
                            {estado === 'error' ? 'Reintentar' : 'Entrar en la Arena'}
                        </button>
                        {onSkip && (
                            <button className="arn-boton arn-boton-fantasma" onClick={onSkip}>
                                Hoy no
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* ── ABRIENDO ────────────────────────────────────────────────── */}
            {estado === 'conectando' && (
                <div className="arn-cargando">
                    <CaraDeSpacy gif={gif} modo={modo} />
                    <p className="arn-cargando-texto">Spacy está preparando tu situación…</p>
                    {/* Nunca sin salida. Si algo se atasca, el alumno se va
                        cuando quiera en vez de recargar la página. */}
                    <button className="arn-boton arn-boton-fantasma" onClick={onSkip}>
                        Cancelar
                    </button>
                </div>
            )}

            {/* ── EN VIVO ─────────────────────────────────────────────────── */}
            {(estado === 'enVivo' || estado === 'cerrando') && (
                <div className="arn-vivo">

                    <div className="arn-vivo-barra">
                        <div className="arn-papel">
                            <span className="arn-etiqueta">Spacy se convirtió en</span>
                            <p className="arn-papel-texto">
                                {papel ? `${papel.emoji} ${papel.es}` : mision?.personaje}
                            </p>
                            {/* La misión ya no tiene caja propia —ese sitio es
                                ahora para lo que dice Spacy— pero no puede
                                desaparecer: es lo que el alumno tiene que
                                conseguir, y sin ella esto es una charla. Se
                                queda aquí, pequeña y siempre a la vista. */}
                            {mision?.mision && (
                                <p className="arn-mision-linea">🎯 {mision.mision}</p>
                            )}
                        </div>
                        <Reloj restante={restante} total={total} />
                    </div>

                    <CaraDeSpacy gif={gif} modo={modo} />

                    <span className="arn-turno">
                        {estado === 'cerrando' ? 'Cerrando…'
                            : oyendo ? 'Te escucha'
                                : hablando ? 'Habla' : 'Tu turno'}
                    </span>

                    <Transcripcion
                        etiqueta="Spacy dice"
                        lineas={susTurnos}
                        vacio="Lo que diga aparecerá aquí."
                        tono="spacy"
                    />

                    <Transcripcion
                        etiqueta="Tú dices"
                        lineas={misTurnos}
                        vacio="Lo que digas aparecerá aquí."
                        tono="alumno"
                    />

                    {/* ── EL ANDAMIO ────────────────────────────────────────
                        Se queda puesto MIENTRAS el alumno habla. Antes se
                        quitaba al oír la primera sílaba y el efecto era el
                        contrario: empezaba a leer, la sugerencia se le
                        desaparecía a media frase y se quedaba colgado.

                        Solo lo sustituyen las siguientes, que llegan cuando el
                        personaje ya ha contestado.

                        Son DOS y llevan a sitios distintos. Con una sola el
                        alumno la copia y no ha decidido nada; con dos tiene que
                        elegir, y elegir obliga a entender las dos. */}
                    {asistido && sugerencias.length > 0 && (
                        <div className="arn-andamio">
                            <span className="arn-etiqueta">Podrías decir</span>
                            <div className="arn-opciones">
                                {sugerencias.map((o, i) => (
                                    <p key={i} className="arn-opcion">{o}</p>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* PARAR Y VER EL RESULTADO.
                        No es un "cancelar": manda `stop`, el backend juzga lo
                        que haya habido y devuelve el recibo igual que si se
                        hubiera acabado el tiempo. Quien corta a los 40 segundos
                        también se ha ganado saber cómo lo hizo. */}
                    {estado === 'enVivo' && (
                        <button className="arn-boton arn-boton-parar" onClick={salir}>
                            ⏹ Parar y ver mi resultado
                        </button>
                    )}

                    {estado === 'cerrando' && (
                        <p className="arn-cargando-texto">
                            Spacy está preparando tu resultado…
                        </p>
                    )}
                </div>
            )}

            {/* ── EL RECIBO ───────────────────────────────────────────────── */}
            {estado === 'veredicto' && <Recibo datos={veredicto} onCerrar={cerrar} />}
        </div>
    );
}
